import { Global, useTheme, ThemeProvider } from '@emotion/react';
import { createContext, useMemo, useState } from 'react';
import getCssStyle from '../styles/style';
import DEFAULT_THEME from '../styles/default_variables';
import useResourceLoader from '../util/useResourceLoader';
import getVariables from '../styles/_variables.css';
import getBootstrapVariables from '../styles/bootstrap/scss/_variables.css';
import { Alert } from 'src/components/ui';
import BootSplashScreen from '../components/BootSplashScreen';

export const UWEThemeContext = createContext();

export function UWEThemeProvider({ children }) {
  const { errorLoadingUweTemplate, loadingUweTemplate = true, uweThemeContextValue } = useUWEThemeProvider();

  return loadingUweTemplate ? (
    <BootSplashScreen />
  ) : errorLoadingUweTemplate ? (
    <Alert color="danger" className="mt-3">
      Error Loading Application.
    </Alert>
  ) : (
    <UWEThemeContext.Provider value={uweThemeContextValue}>
      <ThemeProvider theme={uweThemeContextValue.uweTheme}>
        <UWEThemeGlobals />
        {children}
      </ThemeProvider>
    </UWEThemeContext.Provider>
  );
}

function useUWEThemeProvider() {
  const [uweTemplate, _loadingUweTemplate, errorLoadingUweTemplate] = useResourceLoader(() => window.uwe_template ?? Promise.resolve(), []);
  const loadingUweTemplate = _loadingUweTemplate !== false;

  const [adhocTemplate, setAdhocTemplate] = useState();

  const uweTheme = useMemo(() => buildTheme(DEFAULT_THEME, uweTemplate, adhocTemplate), [buildTheme, DEFAULT_THEME, uweTemplate, adhocTemplate]);

  const uweThemeContextValue = useMemo(
    () => ({
      uweTheme,
      adhocTemplate,
      setAdhocTemplate,
    }),
    [uweTheme, adhocTemplate, setAdhocTemplate]
  );

  return {
    errorLoadingUweTemplate,
    loadingUweTemplate,
    uweThemeContextValue,
  };
}

function buildTheme(...templates) {
  const mergedTemplate = mergedTemplates(templates);

  let $ = buildInitialVars(mergedTemplate);
  $ = getVariables($);
  $ = getBootstrapVariables($);

  return $;
}

function mergedTemplates(templates) {
  return {
    colors: templates.reduce((_, template) => {
      Object.assign(_, template?.colors ?? {});

      return _;
    }, {}),
    font: templates.reduce((_, template) => {
      Object.assign(_, template?.font ?? template?.fonts ?? template?.fontSettings ?? {});

      return _;
    }, {}),
  };
}

function buildInitialVars(mergedTemplate) {
  return {
    theme: {
      ...mergedTemplate,
      colors: Object.entries(mergedTemplate.colors).reduce((acc, [key, value]) => {
        if (/^color_/.test(key)) {
          key = key.replace(/^color_/, '');
        }

        acc[paramCaseToCamelCase(key)] = value;

        return acc;
      }, {}),
      font: Object.entries(mergedTemplate.font).reduce((acc, [key, value]) => {
        acc[paramCaseToCamelCase(key)] = value;

        return acc;
      }, {}),
    },
  };
}

function paramCaseToCamelCase(text) {
  return text.replace(/-(\w)/g, (_, _1) => _1.toUpperCase());
}

export function UWEThemeGlobals() {
  const theme = useTheme();
  const globalStyle = useMemo(() => getCssStyle(theme), [theme]);

  return <Global styles={globalStyle} />;
}
